import { useState } from "react";
import { Download } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Reveal } from "./Reveal";
import { useSite } from "../context/SiteContext";
import { DEFAULT_ROUTINES, ROUTINE_SEMS, ROUTINE_DAYS } from "../data/site";

function getRoutine(settings, sem) {
  const routines = settings.routines || DEFAULT_ROUTINES;
  return routines[sem] || DEFAULT_ROUTINES[sem] || DEFAULT_ROUTINES["Semester I"];
}

export default function RoutineTable() {
  const { settings } = useSite();
  const [sem, setSem] = useState("Semester I");
  const routine = getRoutine(settings, sem);
  const slots = routine.slots || DEFAULT_ROUTINES["Semester I"].slots;

  const cellText = (c) => {
    if (!c) return "";
    if (typeof c === "string") return c;
    return [c.subject, c.room ? `Room: ${c.room}` : "", c.teacher || ""].filter(Boolean).join("\n");
  };

  const downloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const college = settings.college || {};
    doc.setFontSize(16);
    doc.text(college.name || "Kanggui Christian College", 40, 40);
    doc.setFontSize(12);
    doc.text(`Class Routine — ${sem}`, 40, 60);
    const times = routine.times || [];
    const head = [["Day / Period", ...slots]];
    if (times.some(Boolean)) head.push(["Timing", ...slots.map((_, i) => times[i] || "—")]);
    autoTable(doc, {
      startY: 76,
      head,
      body: ROUTINE_DAYS.map((day) => [day, ...slots.map((_, ci) => cellText((routine.days?.[day] || [])[ci]) || "—")]),
      styles: { fontSize: 8, cellPadding: 5, valign: "middle", halign: "center" },
      headStyles: { fillColor: [18, 58, 92], textColor: 255 },
      columnStyles: { 0: { fillColor: [243, 201, 105], textColor: [18, 58, 92], fontStyle: "bold", halign: "left" } },
    });
    doc.save(`routine_${sem.replace(/\s/g, "_")}.pdf`);
  };

  return (
    <section data-testid="routine-section" className="bg-forest text-bone py-24 md:py-36 relative overflow-hidden grain">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Weekly Schedule</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">Class Routine</h2>
          <p className="text-bone/50 mt-5 max-w-xl">The weekly timetable, Monday to Saturday, across seven periods — including room number and teacher.</p>
        </Reveal>

        <div className="flex items-center justify-between gap-4 mt-10 mb-6 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {ROUTINE_SEMS.map((s) => (
              <button key={s} data-testid="routine-sem-tab" onClick={() => setSem(s)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${sem === s ? "bg-gold text-forest border-gold" : "border-bone/25 text-bone/70 hover:border-gold"}`}>
                {s.replace("Semester ", "Sem ")}
              </button>
            ))}
          </div>
          <button data-testid="download-routine-btn" onClick={downloadPDF} className="inline-flex items-center gap-2 bg-bone text-forest px-5 py-2.5 rounded-full text-xs uppercase tracking-widest hover:-translate-y-0.5 transition-transform">
            <Download size={15} /> Download PDF
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] border-collapse">
            <thead>
              <tr>
                <th className="bg-gold text-forest text-left text-xs uppercase tracking-widest px-4 py-3 border border-bone/15">Day / Period</th>
                {slots.map((s, i) => (
                  <th key={i} className="bg-forest-900/50 text-bone/80 text-xs uppercase tracking-widest px-4 py-3 border border-bone/15">{s}</th>
                ))}
              </tr>
              {routine.times && routine.times.some(Boolean) && (
                <tr>
                  <th className="bg-forest-900/60 text-gold-light text-[10px] uppercase tracking-widest px-4 py-2 border border-bone/15 text-left">Timing</th>
                  {slots.map((_, i) => (
                    <th key={i} className="bg-forest-900/30 text-bone/60 text-[11px] px-4 py-2 border border-bone/15 font-normal">{(routine.times || [])[i] || "—"}</th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {ROUTINE_DAYS.map((day) => (
                <tr key={day} data-testid="routine-row">
                  <td className="bg-forest-900/40 font-serif text-lg text-gold-light px-4 py-3 border border-bone/15">{day}</td>
                  {slots.map((_, ci) => {
                    const c = (routine.days?.[day] || [])[ci];
                    const obj = c && typeof c === "object" ? c : { subject: c || "", room: "", teacher: "" };
                    const empty = !obj.subject && !obj.room && !obj.teacher;
                    return (
                      <td key={ci} className="px-3 py-3 border border-bone/15 text-center align-top">
                        {empty ? <span className="text-bone/25">—</span> : (
                          <div className="leading-tight">
                            <p className="text-bone font-medium">{obj.subject || "—"}</p>
                            {obj.room && <p className="text-gold-light text-[11px] mt-1">Room {obj.room}</p>}
                            {obj.teacher && <p className="text-bone/60 text-[11px]">{obj.teacher}</p>}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
